import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Car, CalendarClock, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import EditReservationDialog from "@/components/EditReservationDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS = {
  confirmed: "text-primary border-primary/40 bg-primary/10",
  pending_payment: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  cancelled: "text-red-400 border-red-400/40 bg-red-400/10",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/bookings/me").then((r) => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/quote-options").then((r) => setVehicles(r.data.vehicles)).catch(() => {});
  }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Reservation cancelled.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const cancelLocked = (b) => {
    const pdt = new Date(`${b.quote.date}T${b.quote.time}`);
    return pdt - new Date() < 3600 * 1000; // within 1 hour of pickup
  };

  return (
    <div className="min-h-screen bg-background py-10 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm" data-testid="dash-back">
            <ArrowLeft size={16} /> Home
          </Link>
          <Button variant="ghost" onClick={() => { logout(); navigate("/"); }} data-testid="dash-logout">Logout</Button>
        </div>
        <h1 className="text-3xl sm:text-4xl text-foreground">Welcome, {user?.name}</h1>
        <p className="text-muted-foreground mb-10">Your reservations at a glance.</p>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl text-foreground">My Reservations</h2>
          <Button onClick={() => navigate("/")} data-testid="new-booking-btn"
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase tracking-wide">
            <Plus size={16} className="mr-1" /> New Booking
          </Button>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading...</div>
        ) : bookings.length === 0 ? (
          <div className="border border-white/10 bg-[#121212] p-12 text-center" data-testid="empty-state">
            <Car className="text-primary mx-auto mb-4" size={40} strokeWidth={1.4} />
            <p className="text-foreground mb-2">No reservations yet</p>
            <p className="text-muted-foreground text-sm mb-6">Book your first luxury ride in seconds.</p>
            <Button onClick={() => navigate("/")} className="bg-primary text-primary-foreground rounded-none uppercase">Get a Quote</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((b, i) => (
              <motion.div key={b.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                data-testid={`booking-${i}`} className="border border-white/10 bg-[#121212] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-heading text-lg text-primary">#{b.confirmation_code}</span>
                    <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 border rounded-none ${STATUS[b.status] || "text-muted-foreground border-white/20"}`}>
                      {b.status.replace("_", " ")}
                    </span>
                  </div>
                  <div className="text-foreground">{b.quote.pickup} → {b.quote.dropoff}</div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                    <span className="flex items-center gap-1.5"><CalendarClock size={14} /> {b.quote.date} · {b.quote.time}</span>
                    <span className="flex items-center gap-1.5"><Car size={14} /> {b.quote.vehicle_name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-end">
                  <div className="text-right">
                    <div className="text-2xl font-heading text-primary">${b.quote.total.toFixed(2)}</div>
                  </div>
                  {b.status !== "cancelled" && b.payment_status === "paid" && !cancelLocked(b) && (
                    <EditReservationDialog booking={b} vehicles={vehicles} onDone={load} testId={`edit-btn-${i}`} />
                  )}
                  {b.status !== "cancelled" && (
                    cancelLocked(b) ? (
                      <span data-testid={`cancel-locked-${i}`} className="text-xs text-muted-foreground max-w-[190px] text-right leading-snug">Cancellation closed — within 1 hr of pickup. Call us for help.</span>
                    ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" data-testid={`cancel-btn-${i}`} className="border-white/20 rounded-none hover:border-red-400 hover:text-red-400">Cancel</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#121212] border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-foreground">Cancel this reservation?</AlertDialogTitle>
                          <AlertDialogDescription className="text-muted-foreground">
                            Confirmation #{b.confirmation_code}. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none border-white/20 bg-transparent">Keep it</AlertDialogCancel>
                          <AlertDialogAction data-testid={`confirm-cancel-${i}`} onClick={() => cancel(b.id)} className="rounded-none bg-red-500 hover:bg-red-600 text-white">Yes, cancel</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    )
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
